import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUniversityRankings, calculateNewRank } from '@/services/universityService';
import { useRanking } from '@/context/RankingContext';
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react';

interface RankingDisplayProps {
  className?: string;
}

export const RankingDisplay: React.FC<RankingDisplayProps> = ({ className }) => {
  const {
    isSimulationMode,
    simulatedScore,
    previousScenarioRank,
    setPreviousScenarioRank,
    currentSimulatedRank,
    setCurrentSimulatedRank,
    depaulRank,
    setDepaulRank
  } = useRanking();

  const { data: rankings, isLoading } = useQuery({
    queryKey: ['rankings'],
    queryFn: fetchUniversityRankings,
  });

  /**
   * We'll do the rank calculation inside a custom function
   * then store the result in context states so that other
   * components can display the same rank or rank-change.
   */
  useEffect(() => {
    const updateRank = async () => {
      if (isSimulationMode && simulatedScore !== null) {
        // When entering simulation mode or when score changes, save the previous rank
        if (currentSimulatedRank === null && previousScenarioRank === null) {
          setPreviousScenarioRank(depaulRank);
        } else if (currentSimulatedRank !== null && currentSimulatedRank !== depaulRank) {
          // If we had a previous simulation, save that as previous
          setPreviousScenarioRank(currentSimulatedRank);
        }
        
        const newRank = await calculateNewRank(simulatedScore);
        setCurrentSimulatedRank(newRank);
        setDepaulRank(newRank);
      }
    };

    updateRank();
  }, [isSimulationMode, simulatedScore, setDepaulRank, currentSimulatedRank, setPreviousScenarioRank, previousScenarioRank, depaulRank, setCurrentSimulatedRank]);

  if (isLoading) {
    return (
      <div className={`flex justify-center items-center py-2 ${className || ''}`}>
        <span className="text-gray-500">Loading ranking...</span>
      </div>
    );
  }

  // For display, if we have a simulated rank, use that; else fallback to DB rank
  const displayRank = currentSimulatedRank ?? depaulRank;
  
  // Original rank (before any simulation) - compare against this to show total change
  const originalRank = previousScenarioRank !== null ? previousScenarioRank : depaulRank;
  
  // Calculate the change between original rank and current simulated rank
  let rankChange = 0;
  let isImprovement = false;
  
  if (isSimulationMode && currentSimulatedRank !== null && originalRank !== null) {
    rankChange = originalRank - currentSimulatedRank;
    isImprovement = rankChange > 0; // Lower rank number is better
  }

  return (
    <div className={`flex justify-center items-center py-1 ${className || ''}`}>
      <div className="flex flex-col items-center">
        <span className="text-5xl font-bold text-[#0057B7]">#{displayRank}</span>
        
        {isSimulationMode && rankChange !== 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "flex items-center px-3 py-1.5 rounded-full mt-3",
              isImprovement 
                ? "text-green-900 bg-green-100 dark:text-green-100 dark:bg-green-900/30" 
                : "text-red-900 bg-red-100 dark:text-red-100 dark:bg-red-900/30"
            )}
          >
            {isImprovement ? (
              <ArrowUpIcon className="w-4 h-4 mr-1" />
            ) : (
              <ArrowDownIcon className="w-4 h-4 mr-1" />
            )}
            <span className="font-bold">
              {Math.abs(rankChange)} {isImprovement ? 'ranks up' : 'ranks down'}
            </span>
          </motion.div>
        )}
      </div>
    </div>
  );
};
